import type { RecentSearchesPluginData, SearchParams } from '@algolia/autocomplete-plugin-recent-searches';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { useAlgoliaSearch } from '@algolia-search/composables/useAlgoliaSearch';
import type { HighlightedItem, RecentSearchesItem } from '@algolia-search/composables/useAlgoliaSuggestions/types';
import { INDEX_SUGGESTIONS } from '@algolia-search/consts';
import { useAlgoliaConfigStore } from '@algolia-search/stores/config';
import type { AlgoliaHit } from '@algolia-search/types';
import { ref, useContext } from '@nuxtjs/composition-api';

const HIGHLIGHT_TAG = 'highlight';

export default function useAlgoliaSuggestions() {
  const {
    app: { i18n }
  } = useContext();
  const { getIndex } = useAlgoliaSearch();

  const querySuggestionsIndexName = getIndex(INDEX_SUGGESTIONS);
  const localeCode = i18n.localeProperties.code;
  const storageKey = `algolia-recent-searches-${localeCode}`;

  const query = ref<string>('');
  const lastAppliedQuery = ref<string>('');
  const suggestions = ref<AlgoliaHit[]>([]);
  const recentSearches = ref<RecentSearchesItem[]>([]);
  const { recentSearchesCount } = useAlgoliaConfigStore();

  const searchRecentItems = ({ query, items, limit }: SearchParams<RecentSearchesItem>): HighlightedItem<RecentSearchesItem>[] => {
    // If no query, return the most recent items (up to limit)
    if (!query) {
      return items.slice(0, limit).map((item) => ({
        ...item,
        _highlightResult: {
          query: {
            value: item.query
          }
        }
      }));
    }

    // For any query (even short ones), filter items that match
    const filteredItems = items.filter((item) => item.query.toLowerCase().includes(query.toLowerCase()));

    // Sort by relevance
    const sortedItems = filteredItems.sort((a, b) => {
      // 1. Exact matches first
      const aExact = a.query.toLowerCase() === query.toLowerCase();
      const bExact = b.query.toLowerCase() === query.toLowerCase();

      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      // 2. Sort by length (shortest first)
      const aLength = a.query.length;
      const bLength = b.query.length;

      if (aLength !== bLength) return aLength - bLength;

      // 3. Then by how early the match starts
      const aIndex = a.query.toLowerCase().indexOf(query.toLowerCase());
      const bIndex = b.query.toLowerCase().indexOf(query.toLowerCase());

      if (aIndex !== bIndex) return aIndex - bIndex;

      // 4. Finally by recency (most recent first)
      const timestampA = parseInt(a.id);
      const timestampB = parseInt(b.id);
      return timestampB - timestampA;
    });

    return sortedItems.slice(0, limit).map((item) => ({
      ...item,
      _highlightResult: {
        query: {
          value: highlightText(item.query, query)
        }
      }
    }));
  };

  const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
    key: storageKey,
    // Algolia has no exported Highlighted interface
    // We're forced to suppress this type error
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    search: searchRecentItems
  });

  const removeFromRecentSearches = (id: string) => {
    (recentSearchesPlugin.data as RecentSearchesPluginData<RecentSearchesItem>).removeItem(id);
    fetchRecentSearches(query.value);
  };

  const addToRecentSearches = async (searchTerm: string) => {
    const term = searchTerm.trim();
    if (!term) return;

    const all: RecentSearchesItem[] | Promise<RecentSearchesItem[]> = await (
      recentSearchesPlugin.data as RecentSearchesPluginData<RecentSearchesItem>
    ).getAll();
    const exists = all.find(
      (item) =>
        (item.query && term && item.query.toLowerCase() === term.toLowerCase()) ||
        (item.label && term && item.label.toLowerCase() === term.toLowerCase())
    );

    if (exists) {
      fetchRecentSearches(term);
      return;
    }

    const newItem: RecentSearchesItem = {
      id: `${Date.now()}`,
      query: term,
      label: term
    };

    recentSearchesPlugin.data.addItem(newItem);
    fetchRecentSearches(term);
  };

  const fetchRecentSearches = (searchQuery = '') => {
    const searches: RecentSearchesItem[] | Promise<RecentSearchesItem[]> = recentSearchesPlugin.data.getAll();

    const update = (items: RecentSearchesItem[]) => {
      if (!searchQuery) {
        recentSearches.value = [];
        return;
      }

      // Calculate how many matching items we have
      const matchingItems = items.filter((item) => item.query.toLowerCase().includes(searchQuery.toLowerCase()));

      const dynamicLimit = Math.min(matchingItems.length, recentSearchesCount);

      recentSearches.value = searchRecentItems({
        query: searchQuery,
        items,
        limit: dynamicLimit
      });

      lastAppliedQuery.value = searchQuery;
    };

    if (Array.isArray(searches)) {
      update(searches);
    } else {
      searches.then(update);
    }
  };

  const prepareSuggestions = (searchQuery: string, hits: AlgoliaHit[], queryID: string) => {
    query.value = searchQuery;

    if (searchQuery !== lastAppliedQuery.value) {
      fetchRecentSearches(searchQuery);
    }

    if (!searchQuery || searchQuery.length < 1) {
      suggestions.value = [];
      return;
    }

    const processedSuggestions = hits
      .filter((hit) => !!hit.query)
      .map((hit, index) => ({
        ...hit,
        __queryID: queryID,
        __position: index + 1,
        _highlightResult: {
          query: {
            value: hit._highlightResult?.query?.value ?? highlightText(hit.query, searchQuery)
          }
        }
      }));

    const limitedSuggestions =
      recentSearches.value.length && processedSuggestions.length > 3 ? processedSuggestions.slice(0, 3) : processedSuggestions;

    suggestions.value = [...limitedSuggestions];

    return suggestions.value;
  };

  const highlightText = (text: string, query: string): string => {
    return text.replace(new RegExp(query, 'gi'), (match) => `<span class="${HIGHLIGHT_TAG}">${match}</span>`);
  };

  const formatSuggestion = (hit: AlgoliaHit): RecentSearchesItem => ({
    id: hit.objectID,
    query: hit.query || '',
    label: hit.query || ''
  });

  const selectSuggestion = (item: RecentSearchesItem | AlgoliaHit) => {
    const term = 'query' in item ? item.query || '' : '';
    query.value = term;
    addToRecentSearches(term);
  };

  return {
    query,
    suggestions,
    recentSearches,
    querySuggestionsIndexName,
    selectSuggestion,
    removeFromRecentSearches,
    addToRecentSearches,
    formatSuggestion,
    fetchRecentSearches,
    prepareSuggestions
  };
}
